/**
 * Bettor-facing analytics for the public Bet Detail page.
 * Shows activity, community, board health, and pool liquidity — never house P&L.
 */

import { STATUS_DONE, STATUS_LIVE } from "../components/bondhouse/FixtureStatus";

const num = (v) => Number(v) || 0;

export const RANGES = [
  { id: 7, label: "7D" },
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
  { id: "all", label: "All" },
];

export const CHART_MODES = [
  { id: "cumulative", label: "Total volume" },
  { id: "volume", label: "Daily volume" },
  { id: "tickets", label: "Tickets" },
  { id: "payouts", label: "Winnings paid" },
];

const localDayKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const dayKeyToDate = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const dayKeyLabel = (key) => {
  const dt = dayKeyToDate(key);
  if (!dt) return String(key || "");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const compactCash = (value) => {
  const n = num(value);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${(abs / 1000).toFixed(1)}K`;
  return abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const emptyBucket = (date) => ({
  date,
  placedCount: 0,
  placedStake: 0,
  settledCount: 0,
  winningsPaid: 0,
  cumulativeVolume: 0,
});

const houseCreatedDay = (house) => {
  if (!house?.createdAt) return null;
  return localDayKey(house.createdAt);
};

/** Daily activity timeline — placements + winnings paid (community signal only). */
export const buildActivityTimeline = (house, days = 30) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const wins = Array.isArray(house?.wins) ? house.wins : [];

  const byDay = new Map();
  let oldest = null;

  const touch = (key) => {
    if (!key) return null;
    const dt = dayKeyToDate(key);
    if (!oldest || (dt && dt < oldest)) oldest = dt;
    if (!byDay.has(key)) byDay.set(key, emptyBucket(key));
    return byDay.get(key);
  };

  betAmounts.forEach((entry) => {
    const key = localDayKey(entry.placedAt || entry.createdAt);
    const bucket = touch(key);
    if (!bucket) return;
    bucket.placedCount += 1;
    bucket.placedStake += num(entry.amount);
  });

  wins.forEach((entry) => {
    const key = localDayKey(entry.wonAt || entry.createdAt);
    const bucket = touch(key);
    if (!bucket) return;
    bucket.settledCount += 1;
    bucket.winningsPaid += num(entry.amount);
  });

  if (!byDay.size && !betAmounts.length) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let start;
  if (days === "all") {
    const created = houseCreatedDay(house);
    start = created ? dayKeyToDate(created) : oldest || today;
  } else {
    start = new Date(today);
    start.setDate(start.getDate() - (Number(days) - 1));
    if (oldest && oldest > start) start = oldest;
  }
  if (!start) start = today;

  const series = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const key = localDayKey(cursor);
    series.push(byDay.get(key) || emptyBucket(key));
    cursor.setDate(cursor.getDate() + 1);
  }

  let cumulative = 0;
  return series.map((point) => {
    cumulative += point.placedStake;
    return { ...point, cumulativeVolume: cumulative };
  });
};

export const buildActivityRangeStats = (timeline = []) => {
  let placedStake = 0;
  let placedCount = 0;
  let settledCount = 0;
  let winningsPaid = 0;
  let busiest = null;
  let peakVolume = null;

  for (const p of timeline) {
    placedStake += p.placedStake;
    placedCount += p.placedCount;
    settledCount += p.settledCount;
    winningsPaid += p.winningsPaid;
    if (p.placedCount > 0 && (!busiest || p.placedCount > busiest.placedCount)) busiest = p;
    if (p.placedStake > 0 && (!peakVolume || p.placedStake > peakVolume.placedStake)) {
      peakVolume = p;
    }
  }

  return { placedStake, placedCount, settledCount, winningsPaid, busiest, peakVolume };
};

export const buildCommunityStats = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const wins = Array.isArray(house?.wins) ? house.wins : [];

  const totalTickets = betAmounts.length;
  const totalVolume = betAmounts.reduce((s, e) => s + num(e.amount), 0);
  const totalWinningsPaid = wins.reduce((s, e) => s + num(e.amount), 0);
  const avgStake = totalTickets > 0 ? Math.round(totalVolume / totalTickets) : 0;

  const uniqueBettors = new Set(
    betAmounts.map((e) => e.user?.toString?.() ?? e.user).filter(Boolean)
  ).size;

  const bettorCounts = new Map();
  betAmounts.forEach((e) => {
    const uid = e.user?.toString?.() ?? e.user;
    if (!uid) return;
    bettorCounts.set(uid, (bettorCounts.get(uid) || 0) + 1);
  });
  const repeatBettors = [...bettorCounts.values()].filter((c) => c > 1).length;

  return {
    totalTickets,
    totalVolume,
    totalWinningsPaid,
    avgStake,
    uniqueBettors,
    repeatBettors,
    settledCount: wins.length + (Array.isArray(house?.losses) ? house.losses.length : 0),
  };
};

export const buildBettorInsights = (house, rangeStats, community) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const amounts = betAmounts.map((e) => num(e.amount)).filter((a) => a > 0);

  let biggestStake = null;
  betAmounts.forEach((e) => {
    const amt = num(e.amount);
    if (!biggestStake || amt > biggestStake.amount) {
      biggestStake = { amount: amt, at: e.placedAt || e.createdAt };
    }
  });

  const sorted = [...amounts].sort((a, b) => a - b);
  const median =
    sorted.length > 0
      ? sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)]
      : 0;

  return {
    busiestDay: rangeStats.busiest,
    peakVolumeDay: rangeStats.peakVolume,
    biggestStake,
    medianStake: Math.round(median),
    repeatRate:
      community.uniqueBettors > 0
        ? Math.round((community.repeatBettors / community.uniqueBettors) * 100)
        : null,
  };
};

/** Activity heat label for the hero badge. */
export const activityBadge = (rangeStats, range) => {
  const days = range === "all" ? 30 : Number(range) || 30;
  const perDay = rangeStats.placedCount / Math.max(days, 1);
  if (perDay >= 5) return { label: "Hot", tone: "bd-an-up" };
  if (perDay >= 1) return { label: "Active", tone: "" };
  if (rangeStats.placedCount > 0) return { label: "Quiet", tone: "" };
  return { label: "New", tone: "" };
};

/** League mix from fixtures on the board. */
export const buildLeagueBoardMix = (fixtures = []) => {
  const byLeague = new Map();

  fixtures.forEach((f) => {
    const league = f?.league;
    const key = league?._id || league?.name || "unknown";
    const name = league?.name || "Unknown league";
    const logo = league?.logo || null;
    const short = (f?.status?.short || "").toUpperCase();

    let row = byLeague.get(key);
    if (!row) {
      row = {
        key: String(key),
        name,
        logo,
        fixtureCount: 0,
        live: 0,
        upcoming: 0,
        done: 0,
        bettorsCount: 0,
        markets: 0,
      };
      byLeague.set(key, row);
    }

    row.fixtureCount += 1;
    row.bettorsCount += num(f?.bettorsCount);
    row.markets += num(f?.marketsOffered);
    if (STATUS_LIVE.has(short)) row.live += 1;
    else if (STATUS_DONE.has(short)) row.done += 1;
    else row.upcoming += 1;
  });

  return [...byLeague.values()].sort(
    (a, b) => b.bettorsCount - a.bettorsCount || b.fixtureCount - a.fixtureCount
  );
};

/** Hour-of-day histogram from stake placements (0–23). */
export const buildHourHistogram = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const buckets = Array.from({ length: 24 }, (_, hour) => ({ hour, count: 0, stake: 0 }));

  betAmounts.forEach((entry) => {
    const at = new Date(entry.placedAt || entry.createdAt);
    if (Number.isNaN(at.getTime())) return;
    const hour = at.getHours();
    buckets[hour].count += 1;
    buckets[hour].stake += num(entry.amount);
  });

  const peak = Math.max(...buckets.map((b) => b.count), 1);
  return { buckets, peak };
};

/** Stake size distribution buckets. */
export const buildStakeDistribution = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const amounts = betAmounts.map((e) => num(e.amount)).filter((a) => a > 0);
  if (!amounts.length) return [];

  const edges = [
    { id: "micro", label: "< 500", min: 0, max: 500 },
    { id: "small", label: "500 – 2K", min: 500, max: 2000 },
    { id: "mid", label: "2K – 10K", min: 2000, max: 10000 },
    { id: "large", label: "10K – 50K", min: 10000, max: 50000 },
    { id: "whale", label: "50K+", min: 50000, max: Infinity },
  ];

  const counts = edges.map((edge) => ({
    ...edge,
    count: amounts.filter((a) => a >= edge.min && a < edge.max).length,
  }));

  const peak = Math.max(...counts.map((c) => c.count), 1);
  return counts.map((c) => ({ ...c, pct: Math.round((c.count / amounts.length) * 100), peak }));
};

export const buildAudienceFunnel = (house, pulse) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const bettorCounts = new Map();
  betAmounts.forEach((e) => {
    const uid = e.user?.toString?.() ?? e.user;
    if (!uid) return;
    bettorCounts.set(uid, (bettorCounts.get(uid) || 0) + 1);
  });

  const stages = [
    { id: "views", label: "Views", value: pulse.viewsCount, hint: "Players who checked this house" },
    { id: "followers", label: "Followers", value: pulse.followersCount },
    { id: "bettors", label: "Bettors", value: pulse.uniqueBettors },
    {
      id: "repeat",
      label: "Return players",
      value: [...bettorCounts.values()].filter((c) => c > 1).length,
    },
  ];

  const top = Math.max(...stages.map((s) => s.value), 1);
  return stages.map((stage, i) => {
    const prev = i > 0 ? stages[i - 1].value : null;
    const conv = prev > 0 ? Math.round((stage.value / prev) * 100) : null;
    return {
      ...stage,
      widthPct: Math.max((stage.value / top) * 100, stage.value > 0 ? 6 : 0),
      conv,
    };
  });
};

/** Pool health from a bettor perspective — liquidity, not house economics. */
export const buildPoolHealth = (treasury, house) => {
  const available = num(treasury.available);
  const pending = num(treasury.pending);
  const total = num(treasury.balance) || num(treasury.total) || available + pending;
  const lowThreshold = num(treasury.lowThreshold);

  let status = "healthy";
  let statusLabel = "Healthy";
  if (lowThreshold > 0 && available > 0 && available <= lowThreshold) {
    status = "low";
    statusLabel = "Tight";
  } else if (available > 0 && pending > available * 2) {
    status = "busy";
    statusLabel = "Busy";
  }

  const inPlayPct = total > 0 ? Math.round((pending / total) * 100) : 0;
  const availablePct = total > 0 ? Math.round((available / total) * 100) : 0;

  return {
    status,
    statusLabel,
    total,
    available,
    pending,
    inPlayPct,
    availablePct,
    lowThreshold,
    segments: [
      { key: "available", label: "Available for payouts", value: available, tone: "primary" },
      { key: "pending", label: "In play", value: pending, tone: "warn" },
    ].filter((s) => s.value > 0 || s.key === "available"),
  };
};
